import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { 
  fetchNotifications, 
  markNotificationsRead
} from "../services/notificationsService";

const NotificationContext = createContext(null);

const DEFAULT_NOTIFICATIONS = [
  {
    id: "n1",
    title: "New Product Submitted",
    message: "Nestlé Ceregrow Multigrain has been submitted for review.",
    time: new Date(Date.now() - 10 * 60 * 1000).toISOString(),
    type: "product",
    read: false,
  },
  {
    id: "n2",
    title: "Access Elevation Request",
    message: "Ananya Reddy requested role upgrade to Moderator.",
    time: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    type: "user",
    read: false,
  },
  {
    id: "n3",
    title: "Security Scan Completed",
    message: "System health is optimal. All security logs verified.",
    time: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    type: "success",
    read: true,
  },
];

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load from API
  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return;
    }
    setLoading(true);
    try {
      const type = user.role === "reviewer" ? "reviewer" : null;
      const data = await fetchNotifications({ type, page: 1, limit: 20 });
      
      if (data && Array.isArray(data.items)) {
        const mapped = data.items.map((item) => ({
          id: item.id || item.notification_id || `n_${Math.random()}`,
          title: item.title || item.event_type || "Notification",
          message: item.body || item.message || item.description || "",
          time: item.raised_at || item.created_at || item.time || new Date().toISOString(),
          type: item.type || item.notification_type || "info",
          read: item.is_read !== undefined ? item.is_read : (item.read !== undefined ? item.read : false),
        }));
        setNotifications(mapped);
      } else {
        // Fallback to local storage if API returns empty/invalid list on initial load
        const stored = localStorage.getItem("zytama_notifications");
        if (stored) {
          setNotifications(JSON.parse(stored));
        } else {
          setNotifications(DEFAULT_NOTIFICATIONS);
        }
      }
    } catch (err) {
      console.warn("Failed to fetch notifications from API, loading from localStorage:", err);
      const stored = localStorage.getItem("zytama_notifications");
      if (stored) {
        setNotifications(JSON.parse(stored));
      } else {
        setNotifications(DEFAULT_NOTIFICATIONS);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial load and polling
  // Initial load once on mount or when user shifts
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Sync to localStorage as backup
  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem("zytama_notifications", JSON.stringify(notifications));
    }
  }, [notifications]);

  const addNotification = (notification) => {
    const newNotif = {
      id: `n_${Date.now()}`,
      time: new Date().toISOString(),
      read: false,
      ...notification,
    };
    setNotifications((prev) => [newNotif, ...prev]);
  };

  const markAsRead = async (id) => {
    // Optimistic UI update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    try {
      await markNotificationsRead({ notification_ids: [id], all: false });
    } catch (e) {
      console.warn(`Could not mark notification ${id} as read on server:`, e);
    }
  };

  const markAllAsRead = async () => {
    // Optimistic UI update
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await markNotificationsRead({ notification_ids: [], all: true });
    } catch (e) {
      console.warn("Could not mark all notifications as read on server:", e);
    }
  };

  const clearAll = () => {
    setNotifications([]);
    localStorage.removeItem("zytama_notifications");
  };

  const deleteNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // Helper to generate a fun mock notification
  const triggerMockNotification = () => {
    const mocks = [
      {
        title: "Product Pending Approval",
        message: "Tropicana 100% Orange Juice is awaiting review.",
        type: "product",
      },
      {
        title: "New Editor Joined",
        message: "Sneha Gupta has logged in as Editor.",
        type: "user",
      },
      {
        title: "System Update Completed",
        message: "Zytama Admin Panel upgraded to version 1.1.0.",
        type: "success",
      },
      {
        title: "Database Warning Alert",
        message: "High read query load detected on catalog table.",
        type: "warning",
      },
      {
        title: "FSSAI Check Failed",
        message: "Paper Boat Aam Panna rejected due to missing license details.",
        type: "error",
      },
    ];

    const randomMock = mocks[Math.floor(Math.random() * mocks.length)];
    addNotification(randomMock);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearAll,
        deleteNotification,
        triggerMockNotification,
        refresh: loadNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationProvider");
  }
  return context;
}
